import { FormControl, Input, VStack, Select, Center, Grid, Button, Divider, Text } from "@chakra-ui/react";
import { ErrorMessage, Field, Formik } from "formik";
import { useTeamInvites, useToastNotification } from "lib/hooks";
import { CaretForwardIcon } from "lib/icons/caretForward";
import { DeleteIcon } from "lib/icons/delete";
import { Fragment, useMemo } from "react";
import { TeamInviteType, TeamPermissions } from "lib/types";
import { teamInvitesClient } from "lib/clients";

export const ManageSentSingleInvites = () => {
  const { successToast, errorToast } = useToastNotification();
  const { data: invites, mutate } = useTeamInvites();

  const individualInvites = useMemo(() => {
    return invites
      .filter((invite) => invite.type === TeamInviteType.INDIVIDUAL)
      .sort((a, b) => {
        if (a.match < b.match) { return -1; }
        if (a.match > b.match) { return 1; }
        return 0;
      });
  }, [invites]);

  const changeSentInvitePermission = async ({ target: { value }}, invite): Promise<void> => {
    if (value === invite.permissions) {
      return;
    }

    try {
      await teamInvitesClient.putInvite({ ...invite, permissions: value });
      await mutate();
      successToast({ title: 'Success', description: `Permissions changed for ${invite.match}` });
    } catch (err) {
      errorToast({ title: 'Error', description: err })
    }
  }

  const removeInvite = async (invite): Promise<void> => {
    try {
      await teamInvitesClient.deleteInvite(invite.id);
      await mutate();
      successToast({ title: 'Success', description: `Invite removed for ${invite.match}` });
    } catch (err) {
      errorToast({ title: 'Error', description: err })
    }
  }

  if (!individualInvites.length) {
    return null;
  }

  return (
    <VStack spacing={2} width="100%" className="sent-invites">
      {individualInvites.map((invite, index) => (
        <Fragment key={invite.id}>
          <Formik initialValues={invite} onSubmit={() => null}>
            <Grid templateColumns="30px auto 70px 140px" width="100%" gap="10px">
              <Center>
                <Button onClick={() => removeInvite(invite)} size="sm" variant="destructive" height="30px" width="30px">
                  <DeleteIcon boxSize="12px" />
                </Button>
              </Center>
              <Field name='match'>
                {({ field }) => (
                  <FormControl id="match" isRequired isReadOnly>
                    <Input {...field} size="sm" bg="transparent" variant="readonly" type="email" placeholder="Email" />
                  </FormControl>
                )}
              </Field>
              <Center>
                <Text as="i" color="text.300" size="xs">Invite sent</Text>
              </Center>
              <Field name='permissions'>
                {({ field }) => (
                  <FormControl id="permissions" onChange={(e: any) => changeSentInvitePermission(e, invite)} isRequired>
                    <Select size="sm" variant="primary" {...field} icon={<CaretForwardIcon fill="brand.500" />}>
                      <option value={TeamPermissions.MEMBER}>Member</option>
                      <option value={TeamPermissions.ADMIN}>Admin</option>
                    </Select>
                    <ErrorMessage name='permissions' component="div" className="field-error" />
                  </FormControl>
                )}
              </Field>
            </Grid>
          </Formik>
          <Divider display={(individualInvites.length - 1) === index ? 'none' : 'block' } />
        </Fragment>
      ))}
    </VStack>
  )
};
